import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { supabaseV2 } from "../../lib/supabase";
import { buildRosterLookup, fixEaText, resolveEaPlayerName, type EaLinkRosterRow } from "../../lib/ea-name";
import { currentIntlLocale } from "../../lib/i18n";
import { EmptyState } from "../../components/state";
import { MatchDetailDialog } from "./MatchDetailDialog";
import { track } from "../../lib/analytics";
import { ourClubIdOf } from "../../lib/ea-game";
import type { EaMatch } from "./types";

export function MatchListTable({
  teamId,
  matches,
  eaClubId,
  emptyText,
  pageSize = 15,
}: {
  teamId: string;
  matches: EaMatch[];
  eaClubId: string;
  emptyText: string;
  pageSize?: number;
}) {
  const { t } = useTranslation("mondofc");
  const [page, setPage] = useState(0);
  const [openMatch, setOpenMatch] = useState<EaMatch | null>(null);
  const rosterQuery = useQuery({
    queryKey: ["ea-roster-link", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, ea_sports_id, ea_account_handle")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as EaLinkRosterRow[];
    },
    staleTime: 60_000,
  });
  const rosterLookup = buildRosterLookup(rosterQuery.data || []);
  const totalPages = Math.max(1, Math.ceil(matches.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const visible = matches.slice(start, start + pageSize);
  if (matches.length === 0) return <EmptyState text={emptyText} />;
  return (
    <div className="space-y-2">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left eb-data text-[13px] py-2 pr-2">{t("matchList.date")}</th>
            <th className="text-left eb-data text-[13px] px-1">{t("matchList.opponent")}</th>
            <th className="text-center eb-data text-[13px] px-1">{t("matchList.result")}</th>
            <th className="text-left eb-data text-[13px] px-1 hidden md:table-cell">{t("matchList.topScorer")}</th>
            <th className="text-left eb-data text-[13px] px-1 hidden lg:table-cell">{t("matchList.mvp")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("matchList.avgRating")}</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((m) => {
            const d = m.data || {};
            const ourId = ourClubIdOf(d, eaClubId);
            const clubs = d.clubs || {};
            const players = (d.players || {})[ourId] || {};
            const ourClub = clubs[ourId] || {};
            const oppId = Object.keys(clubs).find((id) => id !== ourId);
            const oppClub = oppId ? clubs[oppId] : null;
            const ourGoals = ourClub?.goals ?? "-";
            const oppGoals = oppClub?.goals ?? "-";
            const resultClass =
              Number(ourGoals) > Number(oppGoals)
                ? "win"
                : Number(ourGoals) < Number(oppGoals)
                  ? "lose"
                  : "draw";
            const playerArr = Object.values(players) as any[];
            const topScorer = playerArr.reduce(
              (best: any, p: any) => (Number(p.goals || 0) > Number(best?.goals || 0) ? p : best),
              playerArr[0] || null
            );
            const mvp =
              playerArr.find((p: any) => Number(p.mom) === 1) ||
              playerArr.reduce(
                (best: any, p: any) => (Number(p.rating || 0) > Number(best?.rating || 0) ? p : best),
                playerArr[0] || null
              );
            const avgRating =
              playerArr.length > 0
                ? (playerArr.reduce((s: number, p: any) => s + Number(p.rating || 0), 0) / playerArr.length).toFixed(2)
                : "-";
            return (
              <tr
                key={m.ea_match_id}
                className="border-t hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() => { track.selectContent("ea_match", m.ea_match_id, { source: "match_list" }); setOpenMatch(m); }}
                title={t("matchList.openMatch")}
              >
                <td className="py-2 pr-2 font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {m.played_at
                    ? new Date(m.played_at).toLocaleDateString(currentIntlLocale(), { day: "2-digit", month: "short", year: "2-digit" })
                    : "-"}
                </td>
                <td className="px-1 truncate max-w-[160px]">
                  {fixEaText(oppClub?.details?.name) || (oppId ? t("matchList.clubFallback", { id: oppId }) : "-")}
                </td>
                <td className="px-1 text-center">
                  <span className={cn("score-chip", resultClass)}>{ourGoals}-{oppGoals}</span>
                </td>
                <td className="px-1 hidden md:table-cell">
                  {topScorer && Number(topScorer.goals || 0) > 0 ? (
                    <span>
                      <span className="font-medium">{resolveEaPlayerName(topScorer, rosterLookup).displayName}</span>
                      <span className="text-muted-foreground font-mono text-xs ml-1.5">×{topScorer.goals}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-1 hidden lg:table-cell">
                  {mvp ? (
                    <span>
                      <span className="font-medium">{resolveEaPlayerName(mvp, rosterLookup).displayName}</span>
                      <span className="text-muted-foreground font-mono text-xs ml-1.5">{Number(mvp.rating || 0).toFixed(1)}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-1 text-right font-tabular hidden md:table-cell">{avgRating}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <MatchDetailDialog
      match={openMatch}
      eaClubId={eaClubId}
      rosterLookup={rosterLookup}
      open={openMatch !== null}
      onOpenChange={(next) => {
        if (!next) setOpenMatch(null);
      }}
    />
    {totalPages > 1 && (
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
        <span>
          {t("matchList.pageRange", { from: start + 1, to: Math.min(start + pageSize, matches.length), total: matches.length })}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          >
            {t("matchList.prev")}
          </Button>
          <span className="font-mono">
            {safePage + 1}/{totalPages}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          >
            {t("matchList.next")}
          </Button>
        </div>
      </div>
    )}
    </div>
  );
}
